// =============================================================================
// ProviderDetailPage — Provider detail with 3 tabs
// =============================================================================

import { useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Card,
  Button,
  Badge,
  Spinner,
  Modal,
  ModalFooter,
} from '@tkpod/ui';
import { hasPermission } from '@tkpod/shared';
import { api } from '@/lib/api';
import { useAuth } from '@/hooks/use-auth';
import { ProviderOverviewTab } from '@/components/provider/ProviderOverviewTab';
import { ProviderWarehousesTab } from '@/components/provider/ProviderWarehousesTab';
import { ProviderMappingsTab } from '@/components/provider/ProviderMappingsTab';
import { ProviderCredentialsTab } from '@/components/provider/ProviderCredentialsTab';
import { ProviderApiAccessTab } from '@/components/provider/ProviderApiAccessTab';
import { ProviderStoreAccessTab } from '@/components/provider/ProviderStoreAccessTab';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface Warehouse {
  id: string;
  name: string;
  code: string | null;
  addressLine1: string;
  addressLine2: string | null;
  city: string;
  state: string | null;
  zip: string;
  country: string;
  timezone: string;
  cutoffLocalTime: string;
  handlingDaysMin: number;
  handlingDaysMax: number;
  isDefault: boolean;
  isActive: boolean;
  createdAt: string;
}

interface Mapping {
  id: string;
  uptId: string;
  uptName: string;
  uptType: string;
  brand: string | null;
  modelName: string | null;
  externalSku: string | null;
  externalProductId: string | null;
  costOverride: string | null;
  capabilities: Record<string, unknown> | null;
  priority: number | null;
  isDefault: boolean;
  isActive: boolean;
  mappedVariants: number;
  totalVariants: number;
  createdAt: string;
}

interface ProviderDetail {
  id: string;
  name: string;
  slug: string;
  type: string;
  status: string;
  capabilities: Record<string, unknown>;
  processingTime: number;
  dispatchDelayMinutes: number;
  notifyShopifyOnLabelCreated: boolean;
  country: string | null;
  timezone: string | null;
  contactEmail: string | null;
  contactPhone: string | null;
  apiEndpoint: string | null;
  notes: string | null;
  createdAt: string;
  updatedAt: string;
  warehouses: Warehouse[];
  mappings: Mapping[];
}

interface ProviderDetailResponse {
  data: ProviderDetail;
}

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const TABS = ['Overview', 'Warehouses', 'UPT Mappings', 'Store access', 'API Credentials', 'API Access'] as const;
type Tab = (typeof TABS)[number];

const STATUS_CONFIG: Record<string, { label: string; variant: 'success' | 'danger' | 'warning' }> = {
  ACTIVE: { label: 'Active', variant: 'success' },
  INACTIVE: { label: 'Inactive', variant: 'danger' },
  MAINTENANCE: { label: 'Maintenance', variant: 'warning' },
};

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function ProviderDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { user } = useAuth();

  const canHardDelete = hasPermission(user?.permissions ?? [], 'provider:hard_delete');
  // Integration credentials are platform-admin only: provider-scoped users
  // never see the tab (the API rejects them server-side as well).
  const canManageCredentials =
    !user?.providerId && hasPermission(user?.permissions ?? [], 'provider:manage');

  const [activeTab, setActiveTab] = useState<Tab>('Overview');
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showArchiveModal, setShowArchiveModal] = useState(false);

  const providerQuery = useQuery<ProviderDetailResponse, Error>({
    queryKey: ['admin', 'provider', id],
    queryFn: () => api.get<ProviderDetailResponse>(`/api/v1/admin/providers/${id}`),
    enabled: !!id,
  });

  const deleteMutation = useMutation({
    mutationFn: async () => api.delete(`/api/v1/admin/providers/${id}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'providers'] });
      navigate('/providers');
    },
  });

  const archiveMutation = useMutation({
    mutationFn: async () =>
      api.patch(`/api/v1/admin/providers/${id}`, { status: 'INACTIVE' }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'provider', id] });
      queryClient.invalidateQueries({ queryKey: ['admin', 'providers'] });
      setShowArchiveModal(false);
    },
  });

  // --- Loading ---------------------------------------------------------------

  if (providerQuery.isLoading) {
    return (
      <div className="flex items-center justify-center py-24">
        <Spinner size="lg" label="Loading provider..." />
      </div>
    );
  }

  // --- Error -----------------------------------------------------------------

  if (providerQuery.isError || !providerQuery.data) {
    return (
      <Card>
        <div className="flex flex-col items-center justify-center gap-4 py-16">
          <p className="text-sm text-danger">
            {providerQuery.error?.message ?? 'Provider not found.'}
          </p>
          <Link to="/providers">
            <Button variant="outline" size="sm">Back to Providers</Button>
          </Link>
        </div>
      </Card>
    );
  }

  const provider = providerQuery.data.data;
  const statusCfg = STATUS_CONFIG[provider.status] ?? { label: provider.status, variant: 'default' as const };
  const visibleTabs = TABS.filter((tab) => {
    // Both integration-secret tabs are platform-admin only; provider-scoped
    // users never see them (the API rejects them server-side as well).
    if (tab === 'API Credentials' || tab === 'API Access') {
      return provider.type === 'EXTERNAL_API' && canManageCredentials;
    }
    if (tab === 'Store access') {
      return provider.type === 'EXTERNAL_API' && provider.slug === 'inkteo';
    }
    return true;
  });

  // --- Render ----------------------------------------------------------------

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Link
            to="/providers"
            className="text-sm text-text-secondary hover:text-text-primary transition-colors"
          >
            &larr; Providers
          </Link>
          <div>
            <div className="flex items-center gap-3">
              <h1 className="text-xl font-semibold text-text-primary">{provider.name}</h1>
              <Badge variant={statusCfg.variant} size="sm" dot>{statusCfg.label}</Badge>
            </div>
            <p className="text-sm text-text-tertiary mt-0.5">
              {provider.type}
              {provider.country && ` · ${provider.country}`}
            </p>
          </div>
        </div>

        <div className="flex items-center gap-2">
          {canHardDelete && (
            <Button variant="danger" size="sm" onClick={() => setShowDeleteModal(true)}>
              Remove Provider
            </Button>
          )}
          {provider.status !== 'INACTIVE' && (
            <Button variant="secondary" size="sm" onClick={() => setShowArchiveModal(true)}>
              Archive
            </Button>
          )}
        </div>
      </div>

      {/* Tabs */}
      <Card padding="none">
        <div className="border-b border-border">
          <nav className="flex -mb-px">
            {visibleTabs.map((tab) => (
              <button
                key={tab}
                type="button"
                onClick={() => setActiveTab(tab)}
                className={`px-6 py-3 text-sm font-medium border-b-2 transition-colors ${
                  activeTab === tab
                    ? 'border-brand-500 text-brand-600'
                    : 'border-transparent text-text-secondary hover:text-text-primary hover:border-border'
                }`}
              >
                {tab}
                {tab === 'Warehouses' && ` (${provider.warehouses.length})`}
                {tab === 'UPT Mappings' && ` (${provider.mappings.length})`}
              </button>
            ))}
          </nav>
        </div>

        <div className="p-6">
          {activeTab === 'Overview' && (
            <ProviderOverviewTab provider={provider} />
          )}
          {activeTab === 'Warehouses' && (
            <ProviderWarehousesTab
              providerId={provider.id}
              warehouses={provider.warehouses}
            />
          )}
          {activeTab === 'UPT Mappings' && (
            <ProviderMappingsTab
              providerId={provider.id}
              mappings={provider.mappings}
            />
          )}
          {activeTab === 'Store access' &&
            provider.type === 'EXTERNAL_API' &&
            provider.slug === 'inkteo' && (
              <ProviderStoreAccessTab
                providerId={provider.id}
                canEdit={canManageCredentials}
              />
            )}
          {activeTab === 'API Credentials' &&
            provider.type === 'EXTERNAL_API' &&
            canManageCredentials && (
              <ProviderCredentialsTab providerId={provider.id} />
            )}
          {activeTab === 'API Access' &&
            provider.type === 'EXTERNAL_API' &&
            canManageCredentials && (
              <ProviderApiAccessTab providerId={provider.id} />
            )}
        </div>
      </Card>

      {/* Archive Confirmation Modal */}
      <Modal
        open={showArchiveModal}
        onClose={() => setShowArchiveModal(false)}
        title="Archive Provider"
        description={`Are you sure you want to archive "${provider.name}"? The provider will be set to Inactive status.`}
        size="sm"
      >
        {archiveMutation.isError && (
          <div className="mb-4 rounded-md bg-red-500/10 border border-red-500/20 px-4 py-3 text-sm text-red-400">
            {archiveMutation.error?.message ?? 'Failed to archive provider.'}
          </div>
        )}
        <ModalFooter>
          <Button variant="outline" onClick={() => setShowArchiveModal(false)}>Cancel</Button>
          <Button
            variant="primary"
            onClick={() => archiveMutation.mutate()}
            loading={archiveMutation.isPending}
          >
            Archive Provider
          </Button>
        </ModalFooter>
      </Modal>

      {/* Delete Confirmation Modal */}
      <Modal
        open={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        title="Remove Provider"
        description={`Are you sure you want to permanently delete "${provider.name}"? This will remove the provider along with all its warehouses and product mappings. This action cannot be undone.`}
        size="sm"
      >
        {deleteMutation.isError && (
          <div className="mb-4 rounded-md bg-red-500/10 border border-red-500/20 px-4 py-3 text-sm text-red-400">
            {deleteMutation.error?.message ?? 'Failed to delete provider.'}
          </div>
        )}
        <ModalFooter>
          <Button variant="outline" onClick={() => setShowDeleteModal(false)}>Cancel</Button>
          <Button
            variant="danger"
            onClick={() => deleteMutation.mutate()}
            loading={deleteMutation.isPending}
          >
            Remove Provider
          </Button>
        </ModalFooter>
      </Modal>
    </div>
  );
}
